import { useRef, useState } from 'react';
import { Camera, Upload, RotateCcw, AlertCircle } from 'lucide-react';

interface FirmaSubirFotoProps {
  onChange: (dataUrl: string | null) => void;
  initialDataUrl?: string | null;
  disabled?: boolean;
}

/**
 * Sube una foto de la firma hecha en papel blanco con lapicero azul.
 *
 * Procesamiento:
 *  - Lee imagen via FileReader → carga en <img>
 *  - La pinta en canvas (max 1200×600 manteniendo aspecto)
 *  - Convierte a PNG dataURL para mandar al backend (mismo formato que canvas dibujado)
 *
 * El backend ya acepta `firma_data_url` PNG, sin cambios.
 */
export function FirmaSubirFoto({ onChange, initialDataUrl, disabled }: FirmaSubirFotoProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(initialDataUrl ?? null);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const procesarArchivo = (file: File) => {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('El archivo debe ser una imagen (JPG, PNG, HEIC)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('La imagen no debe pesar más de 10 MB');
      return;
    }
    setProcessing(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      const img = new Image();
      img.onload = () => {
        // Re-encode en canvas a max 1200×600 para reducir peso
        const MAX_W = 1200;
        const MAX_H = 600;
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        if (w > MAX_W) {
          h = Math.round((h * MAX_W) / w);
          w = MAX_W;
        }
        if (h > MAX_H) {
          w = Math.round((w * MAX_H) / h);
          h = MAX_H;
        }
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const ctx = c.getContext('2d');
        if (!ctx) {
          setError('Tu navegador no soporta procesar la imagen');
          setProcessing(false);
          return;
        }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const png = c.toDataURL('image/png');
        setPreview(png);
        onChange(png);
        setProcessing(false);
      };
      img.onerror = () => {
        setError('No se pudo leer la imagen. Intenta con otro archivo.');
        setProcessing(false);
      };
      img.src = dataUrl;
    };
    reader.onerror = () => {
      setError('Error al leer el archivo');
      setProcessing(false);
    };
    reader.readAsDataURL(file);
  };

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) procesarArchivo(f);
    e.target.value = '';
  };

  const limpiar = () => {
    setPreview(null);
    setError(null);
    onChange(null);
  };

  return (
    <div className="space-y-3">
      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
        <div className="font-semibold mb-1">📝 Cómo subir tu firma:</div>
        <ol className="list-decimal list-inside space-y-0.5">
          <li>Firma sobre <strong>papel blanco</strong> con <strong>lapicero azul</strong>.</li>
          <li>Toma una foto con buena luz, sin sombras sobre la firma.</li>
          <li>Sube el archivo (también puedes tomar la foto desde el celular).</li>
        </ol>
      </div>

      {preview ? (
        <div className="border-2 border-emerald-300 rounded bg-white p-3 flex items-center justify-center min-h-[140px]">
          <img src={preview} alt="Vista previa de tu firma" className="max-h-40 object-contain" />
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded bg-white p-6 text-center min-h-[140px] flex flex-col items-center justify-center gap-2 text-gray-500">
          <Upload size={28} className="opacity-60" />
          <div className="text-xs">Aún no has subido tu firma</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onUpload}
          disabled={disabled}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onUpload}
          disabled={disabled}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={disabled || processing}
          className="bg-arpp-primary hover:bg-arpp-primary/90 text-white text-sm px-4 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Upload size={14} /> {preview ? 'Reemplazar imagen' : 'Subir imagen de firma'}
        </button>
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={disabled || processing}
          className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm px-4 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Camera size={14} /> Tomar foto ahora
        </button>
        {preview && (
          <button
            type="button"
            onClick={limpiar}
            disabled={disabled}
            className="text-gray-700 hover:bg-gray-100 text-sm px-3 py-2 rounded inline-flex items-center gap-1 disabled:opacity-50"
          >
            <RotateCcw size={12} /> Limpiar
          </button>
        )}
        {processing && <span className="text-xs text-gray-600 self-center">Procesando…</span>}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
